import type { UiLocale } from '../core/contracts.js'
import {
  ensureSessionCapability,
  SESSION_HEADER,
} from '../core/session/session-identity.js'
import { clientSessionViewStore } from '../../services/clientSessionViewStore.js'
import { acceptEmbeddedSessionProjection } from './accept-session-projection-response.js'
import { logger } from './logger-client.js'

export interface CanonicalReportArtifactRequest {
  sessionId: string
  reportId: string
  commandId: string
  expectedVersion: number
}

function parseFilenameFromDisposition(disposition: string | null): string | undefined {
  if (!disposition) return undefined
  const match = /filename="([^"]+)"/.exec(disposition)
  return match?.[1]
}

function triggerBlobDownload(url: string, filename: string): boolean {
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.rel = 'noopener noreferrer'
  link.target = '_blank'
  link.style.display = 'none'
  document.body.appendChild(link)
  try {
    link.click()
    return 'download' in link
  } finally {
    document.body.removeChild(link)
  }
}

export async function downloadDiscoveryReportPdf(
  request: CanonicalReportArtifactRequest,
  options?: { filename?: string; locale?: UiLocale },
  retried = false,
): Promise<void> {
  await ensureSessionCapability(request.sessionId)
  const response = await fetch('/api/pdf?action=download', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      [SESSION_HEADER]: request.sessionId,
    },
    body: JSON.stringify({
      ...request,
      ...(options?.locale ? { uiLocale: options.locale } : {}),
    }),
  })

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      message?: string
      projectionEnvelope?: unknown
    }
    const accepted = acceptEmbeddedSessionProjection(body, 'POST /api/pdf?action=download')
    if (response.status === 409 && accepted && !retried) {
      const latest = clientSessionViewStore.getServerProjectionSnapshot()
      if (latest && Number.isSafeInteger(latest.version)) {
        return downloadDiscoveryReportPdf(
          { ...request, expectedVersion: latest.version },
          options,
          true,
        )
      }
    }
    throw new Error(body.message || 'Failed to download PDF')
  }

  const rawResponseVersion = response.headers.get('X-Session-Version')
  const responseVersion = rawResponseVersion === null ? null : Number(rawResponseVersion)
  // A later research write leaves the browser behind. The server still returns
  // 200 and sets this header to the latest session version. The frozen brief is
  // the one named by X-Report-Source-Version, which is often lower than the
  // version the browser sent, so that header is not the match. An older session
  // version than the browser asked for is still a mismatch.
  if (
    responseVersion !== null &&
    Number.isSafeInteger(responseVersion) &&
    responseVersion < request.expectedVersion
  ) {
    throw new Error('Downloaded report does not match the accepted session version')
  }

  const filename =
    options?.filename ||
    parseFilenameFromDisposition(response.headers.get('Content-Disposition')) ||
    'FBC-Proposal-Report.pdf'

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)

  try {
    const downloadSupported = triggerBlobDownload(url, filename)
    if (!downloadSupported) {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  } catch (downloadError) {
    logger.warn('[report-pdf-download] Programmatic download failed, opening blob URL', {
      error: downloadError instanceof Error ? downloadError.message : String(downloadError),
    })
    window.open(url, '_blank', 'noopener,noreferrer')
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
  }
}
